import React, { useCallback, useEffect, useState, useRef } from "react";
import { Form, Table, Button, Badge, Spinner, Row, Col } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router";
import { useSelector } from "react-redux";
import { ROLES } from "../../model/roles";
import useMediaQuery from "../../hooks/useMediaQuery";
import AdminNovitaService from "../../services/AdminNovitaService/AdminNovitaService";
import AgenzieService from "../../services/AgenzieService/AgenzieService";
import { alertService } from "../../services/AlertService";
import "./AdminDashboardPage.scss";

const POLL_MS = 60_000;
/** Righe per richiesta (backend: min 5, max 30). */
const PAGE_LIMITE = 25;

/** Stessa logica lato servizio: tipo minuscolo, id numerico come stringa senza zeri superflui. */
function novitaRowKey(row) {
  if (row == null) return "";
  const tipo = String(row.chiave_tipo || "").toLowerCase().trim();
  const idRaw = row.chiave_id;
  if (idRaw == null) return `${tipo}:`;
  const s = String(idRaw).trim();
  if (s === "") return `${tipo}:`;
  if (/^\d+$/.test(s)) return `${tipo}:${parseInt(s, 10)}`;
  return `${tipo}:${s}`;
}

/** Risposta segna-letto / segna-lavorato (eventuale envelope gateway `data`). */
function ensureNovitaMutationOk(res) {
  if (res == null || typeof res !== "object") return;
  const inner =
    res.data != null && typeof res.data === "object" && !Array.isArray(res.data) ? res.data : res;
  if (inner.success === false) {
    throw new Error(inner.message || "Operazione non riuscita");
  }
}

function isLikelyNovitaRow(o) {
  if (!o || typeof o !== "object") return false;
  const idOk =
    typeof o.chiave_id === "string" ||
    typeof o.chiave_id === "number";
  if (typeof o.chiave_tipo === "string" && idOk) return true;
  return typeof o.tipo_label === "string" && (o.nominativo != null || o.descrizione != null);
}

/**
 * Il gateway / HttpUtils può incapsulare il JSON in modi diversi.
 * Cerchiamo un array di righe feed (vuoto o con chiave_tipo).
 */
function normalizeNovitaFeedPayload(res) {
  if (res == null) return [];
  if (Array.isArray(res)) {
    return res.length === 0 || isLikelyNovitaRow(res[0]) ? res : [];
  }
  if (typeof res !== "object") return [];

  if (typeof res.data === "string") {
    try {
      const inner = JSON.parse(res.data);
      const nested = normalizeNovitaFeedPayload(inner);
      if (Array.isArray(nested)) return nested;
    } catch {
      /* ignore */
    }
  }

  const tryArray = (v) => {
    if (!Array.isArray(v)) return null;
    if (v.length === 0) return v;
    return isLikelyNovitaRow(v[0]) ? v : null;
  };

  const getters = [
    () => tryArray(res.data),
    () => tryArray(res.dati),
    () => tryArray(res.rows),
    () => tryArray(res.items),
    () => tryArray(res.list),
    () => tryArray(res.records),
    () => tryArray(res.result),
    () => tryArray(res.payload),
    () => tryArray(res.risposta),
    () => tryArray(res.body),
    () => tryArray(res.content),
    () => tryArray(res.response),
    () => tryArray(res?.data?.data),
    () => tryArray(res?.data?.rows),
    () => tryArray(res?.data?.list),
    () => tryArray(res?.data?.records),
    () => tryArray(res?.data?.items),
    () => tryArray(res?.payload?.data),
    () => tryArray(res?.result?.data),
    () => tryArray(res?.response?.data),
    () => tryArray(res?.risposta?.data),
    () => tryArray(res?.data?.novita),
    () => tryArray(res?.data?.feed),
  ];

  for (const g of getters) {
    try {
      const a = g();
      if (a) return a;
    } catch {
      /* ignore */
    }
  }

  for (const v of Object.values(res)) {
    const a = tryArray(v);
    if (a) return a;
    if (v && typeof v === "object") {
      for (const inner of Object.values(v)) {
        const b = tryArray(inner);
        if (b) return b;
      }
    }
  }

  return [];
}

/**
 * Appiattisce envelope gateway: es. { data: { data: [], total, has_more } }.
 */
function flattenFeedResponse(res) {
  if (res == null || typeof res !== "object") return res;
  const inner = res.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const hasInnerRows = Array.isArray(inner.data);
    const hasPaging =
      inner.total != null ||
      inner.has_more != null ||
      inner.offset != null ||
      inner.limit != null ||
      inner.limite != null;
    if (hasInnerRows || hasPaging) {
      return { ...res, ...inner };
    }
  }
  return res;
}

/**
 * Risposta feed paginata: `data` + total / offset / limit / has_more.
 * Se l'API è ancora vecchia (solo array grande senza total), taglia lato client alla prima pagina.
 */
function parseNovitaFeedPage(res) {
  const empty = { rows: [], total: 0, offset: 0, limit: PAGE_LIMITE, has_more: false };
  if (res == null || typeof res !== "object") return empty;

  const base = flattenFeedResponse(res);

  let rows;
  if (Array.isArray(base.data) && (base.data.length === 0 || isLikelyNovitaRow(base.data[0]))) {
    rows = base.data;
  } else {
    rows = normalizeNovitaFeedPayload(base);
  }

  const limit = Number(base.limit ?? base.limite) || PAGE_LIMITE;
  let total = Number(base.total);
  if (!Number.isFinite(total) || total < 0) {
    total = 0;
  }
  const offset = Number(base.offset) || 0;

  let has_more =
    typeof base.has_more === "boolean" ? base.has_more : offset + rows.length < total;

  // API legacy o metadati persi: troppi record in un colpo solo → mostra solo una pagina e prova scroll
  if (rows.length > limit && (!Number.isFinite(Number(base.total)) || total <= 0)) {
    rows = rows.slice(0, limit);
    total = rows.length + 1;
    has_more = true;
  }

  // Pagina “piena” ma senza totale in risposta (gateway che perde `total`): altrimenti has_more resta false e non parte mai il caricamento successivo
  const totalFromApi = Number(base.total);
  const hasReliableTotal = Number.isFinite(totalFromApi) && totalFromApi > 0;
  if (
    rows.length >= limit &&
    !hasReliableTotal &&
    typeof base.has_more !== "boolean" &&
    !has_more
  ) {
    has_more = true;
  }

  return { rows, total, offset, limit, has_more };
}

/** Primo antenato scrollabile verticale (layout app con main overflow). */
function getScrollParent(el) {
  if (!el) return null;
  let p = el.parentElement;
  while (p && p !== document.documentElement) {
    const st = window.getComputedStyle(p);
    const oy = st.overflowY;
    if ((oy === "auto" || oy === "scroll" || oy === "overlay") && p.scrollHeight > p.clientHeight + 2) {
      return p;
    }
    p = p.parentElement;
  }
  return null;
}

function isSentinelNearView(el, rootEl, marginPx) {
  if (!el) return false;
  const m = marginPx ?? 320;
  if (rootEl) {
    const er = el.getBoundingClientRect();
    const rr = rootEl.getBoundingClientRect();
    return er.top < rr.bottom + m && er.bottom > rr.top - m;
  }
  const er = el.getBoundingClientRect();
  const h = window.innerHeight || document.documentElement.clientHeight;
  return er.top < h + m && er.bottom > -m;
}

function formatRomeDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", {
    timeZone: "Europe/Rome",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const NOVITA_TIPO_ICONS = {
  iscrizione: { icon: "bi-person-plus-fill", className: "novita-tipo-ico--iscrizione" },
  anagrafica: { icon: "bi-pencil-square", className: "novita-tipo-ico--anagrafica" },
  rendicontazione: { icon: "bi-bank2", className: "novita-tipo-ico--rendicontazione" },
  ticket: { icon: "bi-ticket-perforated", className: "novita-tipo-ico--ticket" },
};

function NovitaTipoIcon({ row }) {
  const k = String(row.azione || row.chiave_tipo || "")
    .toLowerCase()
    .trim();
  const cfg = NOVITA_TIPO_ICONS[k] || { icon: "bi-bell", className: "novita-tipo-ico--default" };
  const label = row.tipo_label || "Novità";
  return (
    <i
      className={`bi ${cfg.icon} novita-tipo-ico ${cfg.className} fs-5`}
      title={label}
      aria-label={label}
    />
  );
}

function iscrizionePagamentoOk(dati) {
  if (dati == null || typeof dati !== "object") return false;
  if (typeof dati.pagamento_ok === "boolean") return dati.pagamento_ok;
  const s = String(dati.stato_pagamento || "")
    .toLowerCase()
    .replace(/[\s_]+/g, "");
  return s === "pagato";
}

function iscrizioneFatturazioneOk(dati) {
  if (dati == null || typeof dati !== "object") return false;
  if (typeof dati.fatturazione_ok === "boolean") return dati.fatturazione_ok;
  const st = String(dati.stato_fatturazione || "").trim();
  if (st && st.toLowerCase() === "fatturato") return true;
  return String(dati.fattura || "").trim() !== "";
}

/** Verifica amministrativa: verde conforme, rosso non conforme, giallo da verificare. */
function iscrizioneVerificaMeta(dati) {
  if (dati == null || typeof dati !== "object") {
    return { code: "pending", label: "Da verificare" };
  }
  const code = dati.verifica_indicatore;
  const label = dati.verifica_indicatore_label;
  if (code === "ok" || code === "bad" || code === "pending") {
    return {
      code,
      label: typeof label === "string" && label.trim() ? label.trim() : "Da verificare",
    };
  }
  const raw = String(dati.stato_verifica_amministrativa || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (raw === "non_conforme") return { code: "bad", label: "Non conforme" };
  if (raw === "conforme") return { code: "ok", label: "Conforme" };
  return { code: "pending", label: "Da verificare" };
}

function verificaDotClassName(code) {
  if (code === "ok") return "admin-novita-dot--ok";
  if (code === "bad") return "admin-novita-dot--ko";
  return "admin-novita-dot--warn";
}

/** Pallini pagamento / fattura / verifica e riga voucher + agenzia (solo iscrizioni). */
function NovitaIscrizioneIndicatori({ dati }) {
  if (!dati) return null;
  const pagOk = iscrizionePagamentoOk(dati);
  const fatOk = iscrizioneFatturazioneOk(dati);
  const verifica = iscrizioneVerificaMeta(dati);
  const voucher = dati.is_voucher === true || dati.is_voucher === 1;
  const agenzia = typeof dati.agenzia_voucher === "string" ? dati.agenzia_voucher.trim() : "";

  const statoPagLabel = String(dati.stato_pagamento || "—").replace(/_/g, " ");
  const statoFatLabel = String(dati.stato_fatturazione || "—").replace(/_/g, " ");
  const titPag = pagOk
    ? `Pagamento registrato (${statoPagLabel})`
    : `Pagamento non completo: ${statoPagLabel}`;
  const titFat = fatOk
    ? `Fatturazione registrata (${statoFatLabel}${dati.fattura ? ` · n. ${dati.fattura}` : ""})`
    : `Fatturazione non registrata: ${statoFatLabel}`;
  const titVer = `Verifica amministrativa: ${verifica.label}`;

  return (
    <div className="admin-novita-iscrizione-meta mt-1 d-flex flex-wrap align-items-center">
      <span
        className="admin-novita-iscrizione-flag small text-muted d-inline-flex align-items-center flex-shrink-0"
        title={titPag}
      >
        <span className="admin-novita-iscrizione-flag__label">Pagamento</span>
        <span
          className={`admin-novita-dot ${pagOk ? "admin-novita-dot--ok" : "admin-novita-dot--ko"}`}
          title={titPag}
          role="img"
          aria-label={`Pagamento: ${titPag}`}
        />
      </span>
      <span
        className="admin-novita-iscrizione-flag small text-muted d-inline-flex align-items-center flex-shrink-0"
        title={titFat}
      >
        <span className="admin-novita-iscrizione-flag__label">Fattura</span>
        <span
          className={`admin-novita-dot ${fatOk ? "admin-novita-dot--ok" : "admin-novita-dot--ko"}`}
          title={titFat}
          role="img"
          aria-label={`Fattura: ${titFat}`}
        />
      </span>
      <span
        className="admin-novita-iscrizione-flag small text-muted d-inline-flex align-items-center flex-shrink-0"
        title={titVer}
      >
        <span className="admin-novita-iscrizione-flag__label">Verifica</span>
        <span
          className={`admin-novita-dot ${verificaDotClassName(verifica.code)}`}
          title={titVer}
          role="img"
          aria-label={`Verifica: ${titVer}`}
        />
      </span>
      {voucher ? (
        <span className="small text-muted text-break w-100">
          <i className="bi bi-ticket-perforated me-1" aria-hidden="true" />
          Iscrizione da voucher
          {agenzia
            ? ` · Agenzia: ${agenzia}`
            : dati.id_agenzia_voucher
              ? ` · Agenzia #${dati.id_agenzia_voucher}`
              : ""}
        </span>
      ) : null}
    </div>
  );
}

/** Allinea alle tab di GestioneCorsiUtentiPage (corsi con / senza esame). */
function mapStatoToActiveTab(statoFormazione, finalExamination) {
  if (finalExamination === 1) {
    if (statoFormazione === "Formazione Conclusa") {
      return "formazioneConclusa";
    }
    if (statoFormazione && String(statoFormazione).includes("Attest")) {
      return "attestatiEmessi";
    }
    return "inCorso";
  }
  if (statoFormazione === "In Attesa Esame") {
    return "inAttesaEsame";
  }
  if (statoFormazione === "Formazione Conclusa") {
    return "archiviati";
  }
  return "inCorso";
}

function buildAdminDashboardContext(row, options = {}, list = {}) {
  const d = row.dati || {};
  return {
    nonce: Date.now() + Math.random(),
    activeTab: mapStatoToActiveTab(d.stato_formazione, d.final_examination),
    filters: {
      cognome: d.cognome || "",
      nome: d.nome || "",
    },
    openVerificaModal: !!options.openVerificaModal,
    id_classe_utente: d.id_classe_utente,
    bookmark: {
      rowKey: novitaRowKey(row),
      tipo_novita: list.tipoFiltro ?? "",
      filtro_pagamento: list.filtroPagamento ?? "pagato",
      cerca_nominativo: list.cercaNominativo ?? "",
      id_agenzia: list.filtroIdAgenzia ?? "",
    },
  };
}

const TIPO_OPTIONS = [
  { value: "", label: "Tutte le novità" },
  { value: "iscrizione", label: "Iscrizioni corsisti attivi" },
  { value: "anagrafica", label: "Modifiche anagrafiche" },
  { value: "rendicontazione", label: "Rendicontazione partner" },
  { value: "ticket", label: "Ticket corsisti" },
];

const PAGAMENTO_FEED_OPTIONS = [
  { value: "pagato", label: "Pagato" },
  { value: "non_pagato", label: "Non pagato" },
  { value: "tutti", label: "Tutte e due" },
];

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.user.userData);
  /** JWT può esporre role come stringa; backend ammette admin (10) e superAdmin (1). */
  const roleId = Number(user?.role);
  const canAccessAdminNovita =
    roleId === ROLES.ADMIN || roleId === ROLES.SUPERADMIN;
  const isWide = useMediaQuery("(min-width: 768px)");

  const [rows, setRows] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [filtroPagamento, setFiltroPagamento] = useState("pagato");
  const [cercaNominativo, setCercaNominativo] = useState("");
  const [cercaDebounced, setCercaDebounced] = useState("");
  const [filtroIdAgenzia, setFiltroIdAgenzia] = useState("");
  const [agenzieOptions, setAgenzieOptions] = useState([]);
  const [workingId, setWorkingId] = useState(null);
  const [bulkLavoratoDevLoading, setBulkLavoratoDevLoading] = useState(false);

  const rowElMapRef = useRef(new Map());
  const pendingScrollRowKeyRef = useRef(null);

  const nextOffsetRef = useRef(0);
  const pagedBeyondFirstRef = useRef(false);
  const sentinelRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);
  const hasMoreRef = useRef(false);
  const loadingInitialRef = useRef(true);
  const rowsRef = useRef([]);
  /** Incrementa a ogni nuovo caricamento “da capo” (cambio filtro / Aggiorna) per scartare risposte async obsolete. */
  const feedRequestIdRef = useRef(0);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingInitialRef.current = loadingInitial;
  }, [loadingInitial]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    const t = setTimeout(() => setCercaDebounced(cercaNominativo.trim()), 400);
    return () => clearTimeout(t);
  }, [cercaNominativo]);

  useEffect(() => {
    if (!canAccessAdminNovita) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await AgenzieService.getAllAgenzie();
        if (!res?.ok || cancelled) {
          return;
        }
        const list = await res.json();
        if (!cancelled && Array.isArray(list)) {
          const sorted = [...list].sort((a, b) =>
            String(a.denominazione ?? "").localeCompare(String(b.denominazione ?? ""), "it", {
              sensitivity: "base",
            })
          );
          setAgenzieOptions(sorted);
        }
      } catch {
        /* elenco opzionale */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccessAdminNovita]);

  useEffect(() => {
    const bm = location.state?.adminDashboardBookmark;
    if (!bm?.rowKey) {
      return;
    }
    if (typeof bm.tipo_novita === "string") {
      setTipoFiltro(bm.tipo_novita);
    }
    if (["pagato", "non_pagato", "tutti"].includes(bm.filtro_pagamento)) {
      setFiltroPagamento(bm.filtro_pagamento);
    }
    if (typeof bm.cerca_nominativo === "string") {
      setCercaNominativo(bm.cerca_nominativo);
      setCercaDebounced(bm.cerca_nominativo.trim());
    }
    if (bm.id_agenzia != null && bm.id_agenzia !== "") {
      setFiltroIdAgenzia(String(bm.id_agenzia));
    }
    pendingScrollRowKeyRef.current = bm.rowKey;
    navigate("/admin-dashboard", { replace: true, state: {} });
  }, [location.state, navigate]);

  useEffect(() => {
    const key = pendingScrollRowKeyRef.current;
    if (!key || loadingInitial) {
      return undefined;
    }
    const el = rowElMapRef.current.get(key);
    if (!el) {
      return undefined;
    }
    pendingScrollRowKeyRef.current = null;
    const raf = window.requestAnimationFrame(() => {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.classList.add("admin-dashboard-novita__row--highlight");
      window.setTimeout(() => el.classList.remove("admin-dashboard-novita__row--highlight"), 4000);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [rows, loadingInitial]);

  const loadInitial = useCallback(async () => {
    if (!canAccessAdminNovita) return;
    const reqId = ++feedRequestIdRef.current;
    pagedBeyondFirstRef.current = false;
    nextOffsetRef.current = 0;
    setLoadingInitial(true);
    try {
      const filtri = {
        offset: 0,
        limite: PAGE_LIMITE,
        filtro_pagamento: filtroPagamento,
        cerca_nominativo: cercaDebounced,
      };
      if (tipoFiltro) {
        filtri.tipo_novita = tipoFiltro;
      }
      const idAg = Number(filtroIdAgenzia);
      if (filtroIdAgenzia && Number.isFinite(idAg) && idAg > 0) {
        filtri.id_agenzia = idAg;
      }
      const res = await AdminNovitaService.feed(filtri);
      if (reqId !== feedRequestIdRef.current) {
        return;
      }
      const parsed = parseNovitaFeedPage(res);
      setRows(parsed.rows);
      setHasMore(parsed.has_more);
      nextOffsetRef.current = parsed.offset + parsed.rows.length;
    } catch (e) {
      if (reqId !== feedRequestIdRef.current) {
        return;
      }
      console.error(e);
      alertService.error(e?.message || "Errore nel caricamento della dashboard", { autoClose: true });
      setRows([]);
      setHasMore(false);
    } finally {
      if (reqId === feedRequestIdRef.current) {
        setLoadingInitial(false);
      }
    }
  }, [canAccessAdminNovita, tipoFiltro, filtroPagamento, cercaDebounced, filtroIdAgenzia]);

  const loadMore = useCallback(async () => {
    if (!canAccessAdminNovita || loadMoreInFlightRef.current || !hasMoreRef.current) {
      return;
    }
    const listGenAtStart = feedRequestIdRef.current;
    loadMoreInFlightRef.current = true;
    pagedBeyondFirstRef.current = true;
    setLoadingMore(true);
    try {
      const filtri = {
        offset: nextOffsetRef.current,
        limite: PAGE_LIMITE,
        filtro_pagamento: filtroPagamento,
        cerca_nominativo: cercaDebounced,
      };
      if (tipoFiltro) {
        filtri.tipo_novita = tipoFiltro;
      }
      const idAgMore = Number(filtroIdAgenzia);
      if (filtroIdAgenzia && Number.isFinite(idAgMore) && idAgMore > 0) {
        filtri.id_agenzia = idAgMore;
      }
      const res = await AdminNovitaService.feed(filtri);
      if (listGenAtStart !== feedRequestIdRef.current) {
        return;
      }
      const parsed = parseNovitaFeedPage(res);
      const prev = rowsRef.current;
      const keys = new Set(prev.map((r) => novitaRowKey(r)));
      const merged = [...prev];
      let added = 0;
      for (const r of parsed.rows) {
        const key = novitaRowKey(r);
        if (!keys.has(key)) {
          keys.add(key);
          merged.push(r);
          added += 1;
        }
      }
      setRows(merged);
      if (parsed.rows.length === 0 || added === 0) {
        setHasMore(false);
      } else {
        setHasMore(parsed.has_more);
        nextOffsetRef.current = parsed.offset + parsed.rows.length;
      }
    } catch (e) {
      if (listGenAtStart === feedRequestIdRef.current) {
        console.error(e);
        alertService.warn(e?.message || "Impossibile caricare altre novità.", { autoClose: true });
      }
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [canAccessAdminNovita, tipoFiltro, filtroPagamento, cercaDebounced, filtroIdAgenzia]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial, tipoFiltro]);

  useEffect(() => {
    if (!canAccessAdminNovita) {
      return undefined;
    }
    const t = setInterval(() => {
      if (pagedBeyondFirstRef.current || loadMoreInFlightRef.current) {
        return;
      }
      loadInitial();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [canAccessAdminNovita, loadInitial]);

  /* Scroll infinito: IntersectionObserver con root=null fallisce se lo scroll è nel layout (main overflow).
   * Usiamo scroll/resize sul window + sul contenitore scrollabile + observer con root corretto. */
  useEffect(() => {
    if (!hasMore || loadingInitial) {
      return undefined;
    }

    let raf = 0;
    const tick = () => {
      if (!hasMoreRef.current || loadMoreInFlightRef.current || loadingInitialRef.current) {
        return;
      }
      const el = sentinelRef.current;
      if (!el) return;
      const rootEl = getScrollParent(el);
      if (isSentinelNearView(el, rootEl, 360)) {
        loadMore();
      }
    };
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };

    const el0 = sentinelRef.current;
    const rootEl = el0 ? getScrollParent(el0) : null;

    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll, { passive: true });
    if (rootEl) {
      rootEl.addEventListener("scroll", onScroll, { passive: true });
    }

    let obs;
    const el = sentinelRef.current;
    if (el) {
      obs = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            loadMore();
          }
        },
        { root: rootEl && rootEl instanceof Element ? rootEl : null, rootMargin: "240px 0px 320px 0px", threshold: 0 }
      );
      obs.observe(el);
    }

    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
      if (rootEl) rootEl.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      if (obs) obs.disconnect();
    };
  }, [hasMore, loadMore, loadingInitial, rows.length]);

  const onSegnaLetto = useCallback(async (row) => {
    try {
      const resLetto = await AdminNovitaService.segnaLetto(row.chiave_tipo, row.chiave_id);
      ensureNovitaMutationOk(resLetto);
      // Scarta risposte feed ancora in volo: altrimenti setRows del feed può sovrascrivere letto/lavorato appena aggiornati.
      feedRequestIdRef.current += 1;
      setLoadingInitial(false);
      setRows((prev) =>
        prev.map((r) =>
          novitaRowKey(r) === novitaRowKey(row) ? { ...r, letto: true } : r
        )
      );
    } catch (e) {
      alertService.error(e?.message || "Operazione non riuscita", { autoClose: true });
    }
  }, []);

  const onBulkLavoratoTripleVerdeDev = useCallback(async () => {
    if (!canAccessAdminNovita) return;
    const confirmed = window.confirm(
      "Pulizia (sviluppo): verranno segnate come lavorate tutte le iscrizioni con Pagamento, Fattura e Verifica tutti a posto, come nelle regole del cruscotto. Procedere?"
    );
    if (!confirmed) return;
    setBulkLavoratoDevLoading(true);
    try {
      const res = await AdminNovitaService.bulkLavoratoTripleVerdeDev();
      if (res?.success) {
        alertService.success(
          `Pulizia eseguita. Candidati: ${res.candidati}. Righe inserite/aggiornate: ${res.inserite_o_aggiornate}.`,
          { autoClose: 9000 }
        );
        await loadInitial();
      } else {
        alertService.error(res?.message || "Operazione non riuscita", { autoClose: true });
      }
    } catch (e) {
      console.error(e);
      alertService.error(e?.message || "Errore durante la pulizia", { autoClose: true });
    } finally {
      setBulkLavoratoDevLoading(false);
    }
  }, [canAccessAdminNovita, loadInitial]);

  const onSegnaLavorato = async (row) => {
    setWorkingId(novitaRowKey(row));
    try {
      const resLav = await AdminNovitaService.segnaLavorato(row.chiave_tipo, row.chiave_id);
      ensureNovitaMutationOk(resLav);
      // Stesso problema del letto: un loadInitial (poll / mount) partito prima può completarsi dopo e ripristinare la riga.
      feedRequestIdRef.current += 1;
      setLoadingInitial(false);
      setRows((prev) => prev.filter((r) => novitaRowKey(r) !== novitaRowKey(row)));
    } catch (e) {
      alertService.error(e?.message || "Operazione non riuscita", { autoClose: true });
    } finally {
      setWorkingId(null);
    }
  };

  const goDettaglio = useCallback(async (row) => {
    await onSegnaLetto(row);
    const d = row.dati || {};
    const listSnap = { tipoFiltro, filtroPagamento, cercaNominativo, filtroIdAgenzia };
    const bookmarkPayload = {
      rowKey: novitaRowKey(row),
      tipo_novita: tipoFiltro,
      filtro_pagamento: filtroPagamento,
      cerca_nominativo: cercaNominativo,
      id_agenzia: filtroIdAgenzia,
    };

    if (row.azione === "iscrizione" || row.azione === "anagrafica") {
      const courseData = {
        id_corso: d.id_corso,
        denominazione: d.nome_corso,
        final_examination: d.final_examination,
        modalita_erogazione: d.modalita_erogazione,
        immagine_corso: d.immagine_corso,
      };
      const openVerifica =
        row.azione === "anagrafica" && d.stato_verifica_amministrativa === "non_conforme";
      navigate("/gestione-corsi-utenti", {
        state: {
          courseData,
          adminDashboardContext: buildAdminDashboardContext(row, { openVerificaModal: openVerifica }, listSnap),
        },
      });
      return;
    }

    if (row.azione === "rendicontazione") {
      navigate("/dashboard-rendicontazione-admin", {
        state: {
          fromAdminNovita: true,
          id_rendicontazione: d.id_rendicontazione,
          adminDashboardBookmark: bookmarkPayload,
        },
      });
      return;
    }

    if (row.azione === "ticket") {
      navigate(`/ticket/${d.id_ticket}`, {
        state: {
          openTicketEditFromAdmin: true,
          adminDashboardBookmark: bookmarkPayload,
        },
      });
    }
  }, [navigate, onSegnaLetto, tipoFiltro, filtroPagamento, cercaNominativo, filtroIdAgenzia]);

  if (!canAccessAdminNovita) {
    return (
      <div className="card custom-card shadow p-4">
        <p className="mb-0">Questa area è riservata al ruolo Amministratore.</p>
      </div>
    );
  }

  return (
    <div className="card custom-card shadow admin-dashboard-novita">
      <div className="card-body">
        <div className="admin-dashboard-novita__toolbar mb-3">
          <h1 className="h4 mb-3 mb-md-2">Dashboard novità</h1>
          <div className="admin-dashboard-novita__filter-grid">
            <Row className="g-2 g-md-3">
              <Col xs={12} md={6} lg={4}>
                <Form.Select
                  className="w-100"
                  size="sm"
                  value={tipoFiltro}
                  onChange={(e) => setTipoFiltro(e.target.value)}
                  aria-label="Filtra per tipo di novità"
                >
                  {TIPO_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <Form.Select
                  className="w-100"
                  size="sm"
                  value={filtroPagamento}
                  onChange={(e) => setFiltroPagamento(e.target.value)}
                  aria-label="Filtra iscrizioni per stato pagamento"
                  title="Per le iscrizioni: mostra solo pagate, non pagate, o tutte. Le altre tipologie di novità restano sempre visibili."
                >
                  {PAGAMENTO_FEED_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      Pagamento: {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <Form.Select
                  className="w-100"
                  size="sm"
                  value={filtroIdAgenzia}
                  onChange={(e) => setFiltroIdAgenzia(e.target.value)}
                  aria-label="Filtra per agenzia"
                  title="Mostra solo righe legate all'agenzia (iscrizione da utente/agenzia, anagrafica e ticket del corsista, rendicontazione). Senza agenzia assegnata la riga non compare con filtro attivo."
                >
                  <option value="">Tutte le agenzie</option>
                  {agenzieOptions.map((ag) => (
                    <option key={ag.id_agenzia} value={String(ag.id_agenzia)}>
                      {ag.denominazione || `Agenzia #${ag.id_agenzia}`}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className="g-2 g-md-3 mt-0 mt-md-1">
              <Col xs={12} sm={6} md={5} lg={4}>
                <Form.Control
                  className="w-100"
                  size="sm"
                  type="search"
                  placeholder="Cerca nome o cognome…"
                  value={cercaNominativo}
                  onChange={(e) => setCercaNominativo(e.target.value)}
                  aria-label="Ricerca per nome o cognome"
                  title="Parole separate da spazio; ordine libero; ignora maiuscole e accenti comuni."
                />
              </Col>
              <Col
                xs={12}
                sm={6}
                md={7}
                lg={8}
                className="d-flex flex-wrap gap-2 align-items-stretch align-items-sm-center admin-dashboard-novita__filter-actions"
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="admin-dashboard-novita__action-btn"
                  onClick={loadInitial}
                  disabled={loadingInitial || loadingMore || bulkLavoratoDevLoading}
                >
                  {loadingInitial && !rows.length ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Aggiorna"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline-warning"
                  size="sm"
                  className="admin-dashboard-novita__action-btn"
                  title="Solo sviluppo: segna lavorate in blocco le iscrizioni con i tre requisiti ok. Rimuovere a release."
                  onClick={onBulkLavoratoTripleVerdeDev}
                  disabled={loadingInitial || loadingMore || bulkLavoratoDevLoading}
                >
                  {bulkLavoratoDevLoading ? <Spinner animation="border" size="sm" /> : "Pulizia iscriz. (dev)"}
                </Button>
              </Col>
            </Row>
          </div>
        </div>

        <div className="table-responsive admin-dashboard-novita__table-wrap">
          <Table hover size="sm" className="align-middle admin-dashboard-novita__table">
            <colgroup>
              <col className="col-nominativo" />
              <col className="col-novita-ico" />
              <col className="col-novita" />
              <col className="col-data" />
              <col className="col-stato" />
              <col className="col-azioni" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="col-nominativo">Nominativo</th>
                <th scope="col" className="col-novita-ico text-center" aria-label="Tipo novità">
                  Tipo
                </th>
                <th scope="col" className="col-novita">Novità</th>
                <th scope="col" className="col-data">Data e ora</th>
                <th scope="col" className="col-stato">Stato</th>
                <th scope="col" className="col-azioni">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loadingInitial && !rows.length ? (
                <tr className="admin-dashboard-novita__tr--plain">
                  <td colSpan={6} className="text-center py-4">
                    <Spinner animation="border" role="status" />
                    <span className="visually-hidden">Caricamento</span>
                  </td>
                </tr>
              ) : !rows.length ? (
                <tr className="admin-dashboard-novita__tr--plain">
                  <td colSpan={6} className="text-center text-muted py-4">
                    Nessuna novità da mostrare.
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map((row) => {
                    const k = novitaRowKey(row);
                    const isBusy = workingId === k;
                    return (
                      <tr
                        key={k}
                        ref={(el) => {
                          const m = rowElMapRef.current;
                          if (el) {
                            m.set(k, el);
                          } else {
                            m.delete(k);
                          }
                        }}
                        className={row.letto ? "table-light" : ""}
                        onClick={() => onSegnaLetto(row)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="col-nominativo" data-label="Nominativo">
                          <span className="admin-dashboard-novita__td-value">{row.nominativo}</span>
                        </td>
                        <td
                          className="col-novita-ico text-center"
                          data-label="Tipo"
                        >
                          <span className="admin-dashboard-novita__td-value d-inline-flex justify-content-end justify-content-md-center w-100">
                            <NovitaTipoIcon row={row} />
                          </span>
                        </td>
                        <td
                          className="col-novita col-novita__body"
                          data-label="Novità"
                        >
                          <span className="d-block small text-muted">{row.tipo_label}</span>
                          <span className="d-block text-break">{row.sottotitolo || row.descrizione}</span>
                          {(row.azione === "iscrizione" || row.chiave_tipo === "iscrizione") && (
                            <NovitaIscrizioneIndicatori dati={row.dati} />
                          )}
                        </td>
                        <td className="col-data" data-label="Data e ora">
                          <span className="admin-dashboard-novita__td-value text-break">
                            {formatRomeDateTime(row.evento_at)}
                          </span>
                        </td>
                        <td className="col-stato" data-label="Stato">
                          <span className="admin-dashboard-novita__td-value">
                            {row.letto ? (
                              <Badge bg="secondary">Letto</Badge>
                            ) : (
                              <Badge bg="warning" text="dark">Da leggere</Badge>
                            )}
                          </span>
                        </td>
                        <td
                          className="col-azioni"
                          data-label="Azioni"
                        >
                          <div
                            className="d-flex flex-wrap gap-1 justify-content-md-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              className="d-inline-flex align-items-center gap-1"
                              onClick={() => goDettaglio(row)}
                              disabled={isBusy}
                              aria-label={`Apri dettaglio: ${row.tipo_label}`}
                            >
                              <i className="bi bi-eye" aria-hidden="true" />
                              {isWide && "Apri"}
                            </Button>
                            <Button
                              type="button"
                              variant="outline-success"
                              size="sm"
                              onClick={() => onSegnaLavorato(row)}
                              disabled={isBusy}
                              aria-label={`Segna come lavorato: ${row.tipo_label}`}
                            >
                              {isBusy ? <Spinner animation="border" size="sm" /> : "Lavorato"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {hasMore ? (
                    <tr ref={sentinelRef} className="admin-dashboard-novita__sentinel" key="__sentinel__">
                      <td colSpan={6} className="text-center py-2 text-muted border-0 bg-transparent">
                        {loadingMore ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" role="status" />
                            <span className="small">Caricamento altre novità…</span>
                          </>
                        ) : (
                          <span className="small">Scorri per caricare altre novità</span>
                        )}
                      </td>
                    </tr>
                  ) : (
                    <tr className="admin-dashboard-novita__tr--plain" key="__end__">
                      <td colSpan={6} className="text-center small text-muted py-2 border-0">
                        Fine elenco
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </Table>
        </div>
        {hasMore && rows.length > 0 && (
          <div className="text-center mt-2">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="py-0"
              onClick={() => loadMore()}
              disabled={loadingMore || loadingInitial}
            >
              {loadingMore ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Caricamento…
                </>
              ) : (
                "Carica altre novità"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
